/**
 * Integrations Service
 * Handles OAuth integrations with third-party services
 */

import { supabase } from '../lib/supabase';

class IntegrationsService {
  /**
   * Get all integrations for a user
   */
  async getUserIntegrations(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_integrations')
        .select('id, provider, provider_email, is_active, last_sync_at, created_at, metadata')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching integrations:', error);
      throw error;
    }
  }

  /**
   * Check if a specific integration is connected
   */
  async isIntegrationConnected(userId, provider) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_integrations')
        .select('is_active')
        .eq('user_id', userId)
        .eq('provider', provider)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking integration:', error);
      return false;
    }
  }

  /**
   * Initiate OAuth flow for a provider
   */
  async connectProvider(provider) {
    try {
      // OAuth configuration for different providers
      const oauthConfigs = {
        google: {
          provider: 'google',
          scopes: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile'
        },
        gmail: {
          provider: 'google',
          scopes: 'https://www.googleapis.com/auth/gmail.readonly'
        },
        notion: {
          // Notion uses custom OAuth - would need backend endpoint
          customFlow: true
        },
        slack: {
          // Slack uses custom OAuth - would need backend endpoint
          customFlow: true
        }
      };

      const config = oauthConfigs[provider];

      if (config?.customFlow) {
        // For custom OAuth flows, redirect to backend endpoint
        const redirectUrl = `${window.location.origin}/settings/integrations`;
        window.location.href = `/api/oauth/${provider}/authorize?redirect=${encodeURIComponent(redirectUrl)}`;
        return;
      }

      // For Google/Gmail, use Supabase OAuth
      if (provider === 'google' || provider === 'gmail') {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: config.provider,
          options: {
            scopes: config.scopes,
            redirectTo: `${window.location.origin}/settings/integrations?provider=${provider}`
          }
        });

        if (error) throw error;
      }
    } catch (error) {
      console.error('Error connecting provider:', error);
      throw error;
    }
  }

  /**
   * Disconnect a provider
   */
  async disconnectProvider(userId, provider) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('user_integrations')
        .update({
          is_active: false,
          access_token: null,
          refresh_token: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('provider', provider);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error disconnecting provider:', error);
      throw error;
    }
  }

  /**
   * Save OAuth tokens (called after OAuth callback)
   */
  async saveIntegration(userId, provider, tokens) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('user_integrations')
        .upsert([{
          user_id: userId,
          provider,
          access_token: tokens.access_token,
          refresh_token: tokens.refresh_token,
          token_expires_at: tokens.expires_at,
          provider_email: tokens.provider_email,
          provider_user_id: tokens.provider_user_id,
          is_active: true,
          scopes: tokens.scopes || [],
          metadata: tokens.metadata || {},
          updated_at: new Date().toISOString()
        }], {
          onConflict: 'user_id,provider'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error saving integration:', error);
      throw error;
    }
  }
}

export default new IntegrationsService();

